import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getOrganizationById, updateOrganization, createOrganization } from '../services/organizationService';
import { taskService } from '../services/taskService'; // Только taskService
import { OrganizationDTO } from '../types/organization';
import { TaskDraftDTO } from '../types/TaskDraftDTO';
// import { StatusTask } from '../types/StatusTask';
import { Button, TextField, Typography, Box, Paper, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Grid } from '@mui/material';
import { AddTask, Edit, Cancel, Save } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { UserType, ManagerRole } from '../types/auth';
import TaskDecompositionModal from '../components/TaskDecompositionModal';
// import { Media } from '../types/media';
import { mediaService } from '../services/mediaService';

const OrganizationDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { state } = useAuth();
    const authenticatedUser = state.user;
    const managerId = authenticatedUser?.type === UserType.MANAGER ? authenticatedUser.manager?.id : authenticatedUser?.user?.id;
    const isPlatformCurator = authenticatedUser?.type === UserType.MANAGER && authenticatedUser.manager?.roles?.includes(ManagerRole.PLATFORM_CURATOR);

    const [organization, setOrganization] = useState<OrganizationDTO | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isNew, setIsNew] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);

    const [editName, setEditName] = useState<string>('');
    const [editInn, setEditInn] = useState<string>('');
    const [editIcon, setEditIcon] = useState<string>('');
    const [editType, setEditType] = useState<number>(0);
    const [editDirectorFullName, setEditDirectorFullName] = useState<string>('');
    const [editLegalAddress, setEditLegalAddress] = useState<string>('');
    const [editKpp, setEditKpp] = useState<string>('');
    const [editOgrn, setEditOgrn] = useState<string>('');

    const [tasks, setTasks] = useState<TaskDraftDTO[]>([]);
    const [tasksLoading, setTasksLoading] = useState<boolean>(true);
    const [tasksError, setTasksError] = useState<string | null>(null);

    const [openCreateTaskModal, setOpenCreateTaskModal] = useState<boolean>(false);
    const [newTaskTitle, setNewTaskTitle] = useState<string>('');
    const [newTaskDescription, setNewTaskDescription] = useState<string>('');
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]); // Состояние для выбранных файлов для нового черновика
    const [uploadingFiles, setUploadingFiles] = useState<boolean>(false); // Состояние для отслеживания загрузки файлов
    
    const [openDecomposeTaskModal, setOpenDecomposeTaskModal] = useState<boolean>(false);
    const [selectedTaskForDecomposition, setSelectedTaskForDecomposition] = useState<TaskDraftDTO | null>(null);

    const handleOpenDecomposeModal = (taskDraft: TaskDraftDTO) => {
        const latestTaskDraft = tasks.find(t => t.id === taskDraft.id); // Находим актуальный черновик задачи
        setSelectedTaskForDecomposition(latestTaskDraft || null); // Используем актуальный, или null если не найден
        setOpenDecomposeTaskModal(true);
    };

    const handleCloseDecomposeModal = () => {
        setOpenDecomposeTaskModal(false);
        setSelectedTaskForDecomposition(null);
    };

    const handleDecomposeSuccess = async () => {
        if (organization?.id) {
            try {
                setTasksLoading(true);
                const updatedTasks = await taskService.getTaskDraftsByOrganization(organization.id!); // Используем taskService
                setTasks(updatedTasks);
            } catch (err) {
                setTasksError('Не удалось обновить список черновиков задач после декомпозиции.');
                console.error(err);
            } finally {
                setTasksLoading(false);
            }
        }
    };

    useEffect(() => {
        if (id === 'new') {
            setIsNew(true);
            setOrganization({
                id: '',
                name: '',
                inn: '',
                icon: '',
                type: 0,
                directorFullName: '',
                legalAddress: '',
                kpp: '',
                ogrn: '',
            });
            setLoading(false);
            return;
        }

        const fetchOrganization = async () => {
            if (!managerId) {
                setError('Не удалось загрузить детали организации: ID менеджера отсутствует.');
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                const data = await getOrganizationById(id!);
                setOrganization(data);
                setEditName(data.name);
                setEditInn(data.inn);
                setEditIcon(data.icon);
                setEditType(data.type);
                setEditDirectorFullName(data.directorFullName || '');
                setEditLegalAddress(data.legalAddress || '');
                setEditKpp(data.kpp || '');
                setEditOgrn(data.ogrn || '');

            } catch (err) {
                setError('Не удалось загрузить детали организации.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchOrganization();
    }, [id, managerId]);

    useEffect(() => {
        console.log('useEffect for tasks triggered. Organization ID:', organization?.id, 'Manager ID:', managerId);
        if (organization?.id) {
            const fetchTasks = async () => {
                console.log('fetchTasks function called. Fetching tasks for Organization ID:', organization.id, 'Manager ID:', managerId);
                try {
                    setTasksLoading(true);
                    const data = await taskService.getTaskDraftsByOrganization(organization.id!); // Используем taskService
                    setTasks(data);
                } catch (err) {
                    setTasksError('Не удалось загрузить черновики задач.');
                    console.error('Error fetching task drafts:', err);
                } finally {
                    setTasksLoading(false);
                }
            };
            fetchTasks();
        }
    }, [organization?.id, managerId]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (isNew) {
            setOrganization((prev: OrganizationDTO | null) => prev ? { ...prev, [name]: value } : null);
        }
    };

    const handleEditFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === 'name') setEditName(value);
        else if (name === 'inn') setEditInn(value);
        else if (name === 'icon') setEditIcon(value);
        else if (name === 'type') setEditType(parseInt(value));
        else if (name === 'directorFullName') setEditDirectorFullName(value);
        else if (name === 'legalAddress') setEditLegalAddress(value);
        else if (name === 'kpp') setEditKpp(value);
        else if (name === 'ogrn') setEditOgrn(value);
    };

    const handleSave = async () => {
        if (!organization || !id) return;
        try {
            const updatedOrganization: OrganizationDTO = {
                ...organization,
                name: editName,
                inn: editInn,
                icon: editIcon,
                type: editType,
                directorFullName: editDirectorFullName,
                legalAddress: editLegalAddress,
                kpp: editKpp,
                ogrn: editOgrn,
            };
            await updateOrganization(updatedOrganization);
            setOrganization(updatedOrganization);
            setIsEditing(false);
        } catch (err) {
            setError('Не удалось сохранить организацию.');
            console.error(err);
        }
    };

    const handleCancelEdit = () => {
        if (organization) {
            setEditName(organization.name);
            setEditInn(organization.inn);
            setEditIcon(organization.icon);
            setEditType(organization.type);
            setEditDirectorFullName(organization.directorFullName || '');
            setEditLegalAddress(organization.legalAddress || '');
            setEditKpp(organization.kpp || '');
            setEditOgrn(organization.ogrn || '');
        }
        setIsEditing(false);
    };

    const handleCreateOrganization = async () => {
        if (!organization) return;
        console.log('Attempting to create organization:', organization);
        try {
            await createOrganization(organization);
            console.log('Organization created successfully!');
            navigate('/dashboard');
        } catch (err) {
            setError('Не удалось создать организацию.');
            console.error('Error creating organization:', err);
        }
    };

    const handleCreateTask = async () => {
        if (!organization?.id || !managerId) return;
        try {
            const newTaskDraft: TaskDraftDTO = {
                title: newTaskTitle,
                description: newTaskDescription,
                organizationId: organization.id,
                managerId: managerId,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };
            setUploadingFiles(true);
            const createdTaskDraft = await taskService.createTaskDraft(newTaskDraft); // Используем createTaskDraft

            if (selectedFiles.length > 0) {
                const uploadPromises = selectedFiles.map(file =>
                    mediaService.uploadFile(file, createdTaskDraft.id, undefined) // Привязываем к новому TaskDraft
                );
                await Promise.all(uploadPromises);
            }
            setOpenCreateTaskModal(false);
            const updatedTasks = await taskService.getTaskDraftsByOrganization(organization.id!); // Используем taskService
            setTasks(updatedTasks);
            setNewTaskTitle('');
            setNewTaskDescription('');
            setSelectedFiles([]);
            setUploadingFiles(false);
        } catch (err) {
            setError('Не удалось создать черновик задачи.');
            console.error(err);
        }
    };

    const handleFileChangeForNewDraft = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            setSelectedFiles(Array.from(event.target.files));
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    }

    if (error) {
        return <Box sx={{ mt: 4 }}><Alert severity="error">{error}</Alert></Box>;
    }

    if (!organization) {
        return <Box sx={{ mt: 4 }}><Alert severity="info">Организация не найдена или не инициализирована.</Alert></Box>;
    }

    return (
        <Box sx={{ p: 3 }}>
            <Grid container spacing={4}>
                {/* Organization Details Section */}
                <Grid item xs={12} lg={6}>
                    <Paper elevation={3} sx={{ p: 4 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h4" gutterBottom>{isNew ? 'Создать Новую Организацию' : 'Детали Организации'}</Typography>
                            {!isNew && !isEditing && (
                                <Button variant="contained" startIcon={<Edit />} onClick={() => setIsEditing(true)}>Редактировать</Button>
                            )}
                            {!isNew && isEditing && (
                                <Box>
                                    <Button variant="outlined" startIcon={<Cancel />} onClick={handleCancelEdit} sx={{ mr: 1 }}>Отмена</Button>
                                    <Button variant="contained" startIcon={<Save />} onClick={handleSave}>Сохранить Изменения</Button>
                                </Box>
                            )}
                        </Box>

                        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
                            <TextField
                                label="Название"
                                name="name"
                                value={isEditing ? editName : organization.name}
                                onChange={isEditing ? handleEditFieldChange : handleChange}
                                fullWidth
                                InputProps={{ readOnly: !isEditing && !isNew }}
                            />
                            <TextField
                                label="ИНН"
                                name="inn"
                                value={isEditing ? editInn : organization.inn}
                                onChange={isEditing ? handleEditFieldChange : handleChange}
                                fullWidth
                                InputProps={{ readOnly: !isEditing && !isNew }}
                            />
                            <TextField
                                label="URL Иконки"
                                name="icon"
                                value={isEditing ? editIcon : organization.icon}
                                onChange={isEditing ? handleEditFieldChange : handleChange}
                                fullWidth
                                InputProps={{ readOnly: !isEditing && !isNew }}
                            />
                            <TextField
                                label="Тип Организации"
                                name="type"
                                type="number"
                                value={isEditing ? editType : organization.type}
                                onChange={isEditing ? handleEditFieldChange : handleChange}
                                fullWidth
                                InputProps={{ readOnly: !isEditing && !isNew }}
                            />
                            <TextField
                                label="ФИО Директора"
                                name="directorFullName"
                                value={isEditing ? editDirectorFullName : (organization.directorFullName || '')}
                                onChange={isEditing ? handleEditFieldChange : handleChange}
                                fullWidth
                                InputProps={{ readOnly: !isEditing && !isNew }}
                            />
                            <TextField
                                label="Юридический Адрес"
                                name="legalAddress"
                                value={isEditing ? editLegalAddress : (organization.legalAddress || '')}
                                onChange={isEditing ? handleEditFieldChange : handleChange}
                                fullWidth
                                InputProps={{ readOnly: !isEditing && !isNew }}
                            />
                            <TextField
                                label="КПП"
                                name="kpp"
                                value={isEditing ? editKpp : (organization.kpp || '')}
                                onChange={isEditing ? handleEditFieldChange : handleChange}
                                fullWidth
                                InputProps={{ readOnly: !isEditing && !isNew }}
                            />
                            <TextField
                                label="ОГРН"
                                name="ogrn"
                                value={isEditing ? editOgrn : (organization.ogrn || '')}
                                onChange={isEditing ? handleEditFieldChange : handleChange}
                                fullWidth
                                InputProps={{ readOnly: !isEditing && !isNew }}
                            />
                            {isNew && (
                                <Button variant="contained" onClick={handleCreateOrganization} sx={{ mt: 2 }}>
                                    Создать Организацию
                                </Button>
                            )}
                            <Button variant="outlined" onClick={() => navigate('/dashboard')} sx={{ mt: 1 }}>
                                Вернуться в Панель Управления
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

                {/* Tasks Section */}
                {!isNew && organization.id && (
                    <Grid item xs={12} lg={6}>
                        <Paper elevation={3} sx={{ p: 4 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Typography variant="h5">Черновики Задач</Typography>
                                <Button 
                                    variant="contained" 
                                    startIcon={<AddTask />}
                                    onClick={() => setOpenCreateTaskModal(true)}
                                >
                                    Создать черновик задачи
                                </Button>
                            </Box>
                            
                            {tasksLoading && <CircularProgress />}
                            {tasksError && <Alert severity="error">Не удалось загрузить черновики задач.</Alert>}
                            {!tasksLoading && tasks.length === 0 && (
                                <Typography>Черновики задач для этой организации не найдены.</Typography>
                            )}

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
                                {tasks.map(taskDraft => (
                                    <Paper key={taskDraft.id} variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box>
                                            <Typography variant="h6">{taskDraft.title}</Typography>
                                            <Typography variant="body2" color="text.secondary">{taskDraft.description}</Typography>
                                            {/* Черновики задач не имеют статуса */}
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            {isPlatformCurator && (
                                                <Button 
                                                    variant="contained" 
                                                    size="small"
                                                    onClick={() => handleOpenDecomposeModal(taskDraft)} 
                                                >
                                                    Декомпозировать
                                                </Button>
                                            )}
                                            {/* Кнопка подробнее для TaskDrafts ведет на отдельную страницу TaskDraftDetailsPage */}
                                            <Button variant="outlined" size="small" onClick={() => navigate(`/task-draft/${taskDraft.id}`)}>Подробнее</Button>
                                        </Box>
                                    </Paper>
                                ))}
                            </Box>
                        </Paper>
                    </Grid>
                )}
            </Grid>

            {/* Create Task Draft Modal */}
            <Dialog open={openCreateTaskModal} onClose={() => setOpenCreateTaskModal(false)} fullWidth maxWidth="sm">
                <DialogTitle>Создать новый черновик задачи для {organization?.name}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Название черновика задачи"
                        type="text"
                        fullWidth
                        value={newTaskTitle}
                        onChange={(e) => setNewTaskTitle(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Описание черновика задачи"
                        type="text"
                        fullWidth
                        multiline
                        rows={4}
                        value={newTaskDescription}
                        onChange={(e) => setNewTaskDescription(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Прикрепить Медиафайлы</Typography>
                    <Box sx={{ mb: 2 }}>
                        <input
                            accept="image/*,application/pdf"
                            style={{ display: 'none' }}
                            id="create-draft-upload-media-button"
                            multiple
                            type="file"
                            onChange={handleFileChangeForNewDraft}
                        />
                        <label htmlFor="create-draft-upload-media-button">
                            <Button variant="contained" component="span">
                                Выбрать Файлы
                            </Button>
                        </label>
                        {selectedFiles.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="body2">Выбрано файлов: {selectedFiles.length}</Typography>
                                <Button variant="outlined" onClick={() => setSelectedFiles([])} sx={{ ml: 1 }}>
                                    Очистить Выбор
                                </Button>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCreateTaskModal(false)} color="primary">Отмена</Button>
                    <Button onClick={handleCreateTask} color="primary" variant="contained" disabled={uploadingFiles}>
                        {uploadingFiles ? <CircularProgress size={24} /> : 'Создать'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Task Decomposition Modal */}
            <TaskDecompositionModal
                open={openDecomposeTaskModal}
                onClose={handleCloseDecomposeModal}
                task={selectedTaskForDecomposition} 
                onDecomposeSuccess={handleDecomposeSuccess}
            />
        </Box>
    );
};

export default OrganizationDetailsPage;
